import { Injectable, OnDestroy } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, interval, timer, race, Subscription } from 'rxjs';
import {
  map,
  catchError,
  switchMap,
  tap,
  withLatestFrom,
  takeUntil,
  filter,
  take,
} from 'rxjs/operators';
import { Router } from '@angular/router';
import * as OnboardingActions from './onboarding.actions';
import * as OnboardingSelectors from './onboarding.selectors';
import * as IcpSelectors from '../icp/icp.selectors';
import { loginSuccess, updateUser } from '../auth/auth.actions';
import { selectToken } from '../auth/auth.selectors';
import { OnboardingService } from '../../services/onboarding.service';
import { EchoService } from '../../services/echo.service';
import { PopupService } from '../../services/popup.service';
import { AppState } from '../reducers';

const MISSING_SESSION_MESSAGE =
  'Your onboarding session was lost. Please reload the page and try again.';

// Must outlast the backend pipeline: the Firecrawl webhook deadline plus the merged AI
// call that follows it.
const EXTRACTION_POLL_BUDGET_MS = 150000;

@Injectable()
export class OnboardingEffects implements OnDestroy {
  private echoSubscription: Subscription | null = null;

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private onboardingService: OnboardingService,
    private echoService: EchoService,
    private popupService: PopupService,
    private router: Router,
  ) {}

  ngOnDestroy(): void {
    this.cleanupEchoSubscription();
  }

  private cleanupEchoSubscription(): void {
    if (this.echoSubscription) {
      this.echoSubscription.unsubscribe();
      this.echoSubscription = null;
    }
  }

  // ============================================
  // Session Init Effects
  // ============================================

  initSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OnboardingActions.initOnboardingSession),
      switchMap(() =>
        this.onboardingService.initSession().pipe(
          map((response) =>
            OnboardingActions.initOnboardingSessionSuccess({
              sessionId: response.session_id,
              currentStep: response.current_step,
              expiresAt: response.expires_at,
            }),
          ),
          catchError((error) =>
            of(OnboardingActions.initOnboardingSessionFailure({ error })),
          ),
        ),
      ),
    ),
  );

  saveSessionOnInit$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OnboardingActions.initOnboardingSessionSuccess),
        tap((action) => {
          this.onboardingService.saveSessionToStorage({
            sessionId: action.sessionId,
            currentStep: action.currentStep,
            expiresAt: action.expiresAt,
            stepData: {},
          });
        }),
      ),
    { dispatch: false },
  );

  // ============================================
  // Session Resume Effects
  // ============================================

  resumeSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OnboardingActions.resumeOnboardingSession),
      switchMap((action) =>
        this.onboardingService.resumeSession(action.sessionId).pipe(
          map((response) =>
            OnboardingActions.resumeOnboardingSessionSuccess({
              sessionId: response.session_id,
              currentStep: response.current_step,
              expiresAt: response.expires_at,
              stepData: response.step_data,
              extractionStatus: response.extraction_status,
              productDescription: response.product_description,
              companyName: response.company_name,
              benefits: response.benefits,
              pains: response.pains,
            }),
          ),
          catchError((error) => {
            // If session not found or expired (404, 410), init a new session
            if (error.status === 404 || error.status === 410) {
              this.onboardingService.clearSessionFromStorage();
              return of(OnboardingActions.initOnboardingSession());
            }
            return of(
              OnboardingActions.resumeOnboardingSessionFailure({ error }),
            );
          }),
        ),
      ),
    ),
  );

  saveSessionOnResume$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OnboardingActions.resumeOnboardingSessionSuccess),
        tap((action) => {
          this.onboardingService.saveSessionToStorage({
            sessionId: action.sessionId,
            currentStep: action.currentStep,
            expiresAt: action.expiresAt,
            stepData: action.stepData,
          });
        }),
      ),
    { dispatch: false },
  );

  // Resume polling if extraction is still in progress after session resume
  resumePollingOnResume$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OnboardingActions.resumeOnboardingSessionSuccess),
      filter(
        (action) =>
          action.extractionStatus === 'pending' ||
          action.extractionStatus === 'processing',
      ),
      map(() => OnboardingActions.pollExtractionStatus()),
    ),
  );

  // ============================================
  // Update Step Effects
  // ============================================

  updateStep$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OnboardingActions.updateOnboardingStep),
      withLatestFrom(
        this.store.select(OnboardingSelectors.selectOnboardingSessionId),
      ),
      switchMap(([action, sessionId]) => {
        if (!sessionId) {
          return of(
            OnboardingActions.updateOnboardingStepFailure({
              error: { message: MISSING_SESSION_MESSAGE },
            }),
          );
        }

        return this.onboardingService
          .updateStep(sessionId, action.step, action.data)
          .pipe(
            map((response) =>
              OnboardingActions.updateOnboardingStepSuccess({
                step: action.step,
                data: action.data,
                stepData: response.step_data,
              }),
            ),
            catchError((error) =>
              of(OnboardingActions.updateOnboardingStepFailure({ error })),
            ),
          );
      }),
    ),
  );

  saveStepDataToStorage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OnboardingActions.updateOnboardingStepSuccess),
        tap((action) => {
          this.onboardingService.updateStoredSessionStep(action.step);
          this.onboardingService.updateStoredSessionStepData(
            `step${action.step}`,
            action.data,
          );
        }),
      ),
    { dispatch: false },
  );

  // ============================================
  // Extract Product Effects
  // ============================================

  extractProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OnboardingActions.extractProduct),
      withLatestFrom(
        this.store.select(OnboardingSelectors.selectOnboardingSessionId),
      ),
      switchMap(([action, sessionId]) => {
        if (!sessionId) {
          return of(
            OnboardingActions.extractProductFailure({
              error: { message: MISSING_SESSION_MESSAGE },
            }),
          );
        }

        return this.onboardingService
          .extractProduct(sessionId, action.websiteUrl, action.companyName)
          .pipe(
            switchMap((response) => [
              OnboardingActions.extractProductSuccess({
                extractionStatus: response.extraction_status,
                message: response.message,
              }),
              OnboardingActions.pollExtractionStatus(),
            ]),
            catchError((error) =>
              of(OnboardingActions.extractProductFailure({ error })),
            ),
          );
      }),
    ),
  );

  // ============================================
  // Extraction Status Polling Effects
  // ============================================

  // Poll extraction status every 5 seconds until completed/failed.
  //
  // The budget has to cover the backend's whole pipeline: the Firecrawl webhook deadline
  // (75s by default) plus the merged AI call that runs after it. A shorter window made
  // slow crawls look like failures even though the extraction eventually landed.
  pollExtractionStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OnboardingActions.pollExtractionStatus),
      withLatestFrom(
        this.store.select(OnboardingSelectors.selectOnboardingSessionId),
      ),
      switchMap(([_, sessionId]) => {
        if (!sessionId) {
          return of(OnboardingActions.stopPollingExtractionStatus());
        }

        const timeout$ = timer(EXTRACTION_POLL_BUDGET_MS).pipe(
          map(() => OnboardingActions.extractionTimeout()),
        );

        // Stop signals
        const stop$ = this.actions$.pipe(
          ofType(
            OnboardingActions.stopPollingExtractionStatus,
            OnboardingActions.pollExtractionStatusSuccess,
            OnboardingActions.pollExtractionStatusFailure,
            OnboardingActions.extractionTimeout,
            OnboardingActions.resetOnboardingState,
          ),
        );

        // Poll every 5 seconds
        const poll$ = interval(5000).pipe(
          takeUntil(stop$),
          switchMap(() =>
            this.onboardingService.getProductStatus(sessionId).pipe(
              map((response) => {
                if (response.extraction_status === 'completed') {
                  return OnboardingActions.pollExtractionStatusSuccess({
                    extractionStatus: 'completed',
                    extractionSource: response.extraction_source,
                    productDescription: response.product_description,
                    companyName: response.company_name,
                    benefits: response.benefits,
                    pains: response.pains,
                  });
                }

                if (response.extraction_status === 'failed') {
                  return OnboardingActions.pollExtractionStatusFailure({
                    error: response.errors?.[0] || 'Extraction failed',
                  });
                }

                // Still processing
                return OnboardingActions.extractionStillProcessing({
                  extractionStatus: response.extraction_status,
                });
              }),
              catchError((error) =>
                of(OnboardingActions.pollExtractionStatusFailure({ error })),
              ),
            ),
          ),
        );

        return race(poll$, timeout$).pipe(takeUntil(stop$));
      }),
    ),
  );

  // Immediate initial check (don't wait 5 seconds for first poll)
  initialExtractionCheck$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OnboardingActions.pollExtractionStatus),
      withLatestFrom(
        this.store.select(OnboardingSelectors.selectOnboardingSessionId),
      ),
      switchMap(([_, sessionId]) => {
        if (!sessionId) {
          return of(OnboardingActions.stopPollingExtractionStatus());
        }

        return this.onboardingService.getProductStatus(sessionId).pipe(
          map((response) => {
            if (response.extraction_status === 'completed') {
              return OnboardingActions.pollExtractionStatusSuccess({
                extractionStatus: 'completed',
                extractionSource: response.extraction_source,
                productDescription: response.product_description,
                companyName: response.company_name,
                benefits: response.benefits,
                pains: response.pains,
              });
            }

            if (response.extraction_status === 'failed') {
              return OnboardingActions.pollExtractionStatusFailure({
                error: response.errors?.[0] || 'Extraction failed',
              });
            }

            return OnboardingActions.extractionStillProcessing({
              extractionStatus: response.extraction_status,
            });
          }),
          catchError((error) =>
            of(OnboardingActions.pollExtractionStatusFailure({ error })),
          ),
        );
      }),
      take(1),
    ),
  );

  // ============================================
  // Complete Onboarding Effects
  // ============================================

  completeOnboarding$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OnboardingActions.completeOnboarding),
      withLatestFrom(
        this.store.select(OnboardingSelectors.selectOnboardingSessionId),
      ),
      switchMap(([action, sessionId]) => {
        // The reducer has already flipped complete.isLoading, so a missing session has to
        // produce a failure action. Dropping the action here left the button reading
        // "Completing..." forever with no request and no error.
        if (!sessionId) {
          return of(
            OnboardingActions.completeOnboardingFailure({
              error: { message: MISSING_SESSION_MESSAGE },
            }),
          );
        }

        return this.onboardingService
          .completeOnboarding(sessionId, action.data)
          .pipe(
            map((response) =>
              OnboardingActions.completeOnboardingSuccess({
                result: {
                  user: response.user,
                  icp: response.icp,
                  workspace: response.workspace,
                  token: response.token,
                  lead_generation_status: response.lead_generation_status,
                },
              }),
            ),
            catchError((error) =>
              of(OnboardingActions.completeOnboardingFailure({ error })),
            ),
          );
      }),
    ),
  );

  // Update auth state with new user data, navigate to dashboard, and start listening for leads.
  // IMPORTANT: We dispatch updateUser FIRST (synchronously updates the store) before navigating,
  // so the onboardingGuard on /dashboard sees has_completed_setup = true.
  handleCompleteSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OnboardingActions.completeOnboardingSuccess),
      withLatestFrom(this.store.select(selectToken)),
      tap(([action, existingToken]) => {
        // Clear onboarding session from storage
        this.onboardingService.clearSessionFromStorage();

        const user = { ...action.result.user, has_completed_setup: true };

        // The interceptor reads its bearer token from the persisted `auth` slice, so a
        // token returned by `complete` has to land there — writing it to a standalone
        // localStorage key left the user unauthenticated on the dashboard.
        if (!existingToken && action.result.token) {
          this.store.dispatch(
            loginSuccess({ token: action.result.token, user }),
          );
        } else {
          // Update auth state FIRST so guards see has_completed_setup = true
          this.store.dispatch(updateUser({ user }));
        }

        // Navigate to dashboard (guard will now pass because auth state is updated)
        this.popupService.notify(
          'Welcome! Your leads are being generated.',
          'Success',
          'success',
        );
        this.router.navigate(['/dashboard']);
      }),
      // Dispatch lead listening action (non-blocking, after navigation)
      map(([action]) =>
        OnboardingActions.startListeningForLeads({
          userId: action.result.user.id,
        }),
      ),
    ),
  );

  // ============================================
  // WebSocket Lead Listening Effects
  // ============================================

  startListeningForLeads$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OnboardingActions.startListeningForLeads),
        tap((action) => {
          this.cleanupEchoSubscription();

          const echo = this.echoService.getEcho();
          if (!echo) {
            console.warn('Echo not available for lead listening');
            return;
          }

          const channel = echo.private(`onboarding-leads.${action.userId}`);
          channel.listen('.onboarding-lead.generated', (event: any) => {
            this.store.dispatch(
              OnboardingActions.leadGenerated({
                lead: event.lead || event,
                generationStatus: event.generation_status ?? null,
              }),
            );
          });
        }),
      ),
    { dispatch: false },
  );

  stopListeningForLeads$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OnboardingActions.stopListeningForLeads),
        tap(() => {
          const echo = this.echoService.getEcho();
          if (echo) {
            // Leave all onboarding lead channels
            try {
              echo.leaveAllChannels();
            } catch (e) {
              console.error('Error leaving Echo channels:', e);
            }
          }
        }),
      ),
    { dispatch: false },
  );

  // ============================================
  // Error Handling Effects
  // ============================================

  handleInitFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OnboardingActions.initOnboardingSessionFailure),
        tap(() => {
          this.popupService.notify(
            'Failed to start onboarding session. Please try again.',
            'Error',
            'error',
          );
        }),
      ),
    { dispatch: false },
  );

  handleResumeFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OnboardingActions.resumeOnboardingSessionFailure),
        tap(() => {
          this.popupService.notify(
            'Failed to resume onboarding session.',
            'Error',
            'error',
          );
        }),
      ),
    { dispatch: false },
  );

  handleUpdateStepFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OnboardingActions.updateOnboardingStepFailure),
        tap((action) => {
          this.popupService.notify(
            action.error?.message ||
              'Failed to save step data. Please try again.',
            'Error',
            'error',
          );
        }),
      ),
    { dispatch: false },
  );

  handleExtractionFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          OnboardingActions.extractProductFailure,
          OnboardingActions.pollExtractionStatusFailure,
        ),
        withLatestFrom(
          this.store.select(OnboardingSelectors.selectCompleteResult),
          this.store.select(IcpSelectors.selectDefaultIcp),
          this.store.select(IcpSelectors.selectIcps),
        ),
        tap(([, completeResult, defaultIcp, icps]) => {
          this.popupService.notify(
            'Product extraction is still processing. You can complete your ICP to start scoring leads.',
            'Info',
            'info',
            999999999,
            {
              url: this.icpDetailUrl(completeResult, defaultIcp, icps),
              text: 'Complete ICP',
            },
          );
        }),
      ),
    { dispatch: false },
  );

  handleExtractionTimeout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OnboardingActions.extractionTimeout),
        withLatestFrom(
          this.store.select(OnboardingSelectors.selectCompleteResult),
          this.store.select(IcpSelectors.selectDefaultIcp),
          this.store.select(IcpSelectors.selectIcps),
        ),
        tap(([, completeResult, defaultIcp, icps]) => {
          this.popupService.notify(
            'Product extraction is still processing. You can complete your ICP to start scoring leads.',
            'Info',
            'info',
            999999999,
            {
              url: this.icpDetailUrl(completeResult, defaultIcp, icps),
              text: 'Complete ICP',
            },
          );
        }),
      ),
    { dispatch: false },
  );

  /**
   * Prefer the ICP created by onboarding completion, then the default ICP from the list.
   * `/icp/create` is only a last resort for the rare case neither exists yet.
   */
  private icpDetailUrl(
    completeResult: { icp?: { id?: number } } | null,
    defaultIcp: { id?: number } | null | undefined,
    icps: Array<{ id?: number }> | null | undefined,
  ): string {
    const icpId = completeResult?.icp?.id ?? defaultIcp?.id ?? icps?.[0]?.id;

    return icpId ? `/icp/detail/${icpId}` : '/icp/create';
  }

  handleCompleteFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OnboardingActions.completeOnboardingFailure),
        tap((action) => {
          let message = 'Failed to complete onboarding. Please try again.';
          if (action.error?.status === 422) {
            message = 'Please fill in all required fields.';
          }
          this.popupService.notify(message, 'Error', 'error');
        }),
      ),
    { dispatch: false },
  );

  // ============================================
  // Reset Effects
  // ============================================

  clearStorageOnReset$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OnboardingActions.resetOnboardingState),
        tap(() => {
          this.onboardingService.clearSessionFromStorage();
          this.cleanupEchoSubscription();
        }),
      ),
    { dispatch: false },
  );
}
